import React, { useEffect, useState, useCallback, useMemo } from 'react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { ff } from '@atlassian/jira-feature-flagging-using-meta';
import { componentWithCondition } from '@atlassian/jira-feature-flagging-utils/src/utils/component-with-condition/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import {
	useAnalyticsEvents,
	fireOperationalAnalytics,
} from '@atlassian/jira-product-analytics-bridge';
import RemoteActionsManager, { type LifecycleEventHandler } from '@atlassian/remote-actions';
import { createLocalStorageProvider } from '@atlassian/jira-browser-storage-providers/src/controllers/local-storage/index.tsx';
import { isFedRamp } from '@atlassian/atl-context';
import { useIsStagingOrDevOrLocal } from '@atlassian/jira-tenant-context-controller/src/components/environment/index.tsx';

// 10 min polling interval
const DEFAULT_POLLING_INTERVAL = 600000;
const DEBUG_MODE_POLLING_INTERVAL = 10000;
type DebugModeProps = {
	product: string;
	pollingInterval: number;
};

const storageProvider = createLocalStorageProvider('', false);

const getDebugModeFromLocalStorage = (): boolean => {
	try {
		let isDebugModeSetInLocalStorage;
		if (fg('jfp-vulcan-browser-storage-migration')) {
			isDebugModeSetInLocalStorage = storageProvider.get('remote-actions-debug-mode');
		} else {
			isDebugModeSetInLocalStorage = localStorage.getItem('remote-actions-debug-mode');
		}
		return isDebugModeSetInLocalStorage === 'true';
	} catch (e) {
		return false;
	}
};

const getDebugModeProps = (isRemoteActionsDebugMode: boolean): DebugModeProps => {
	if (isRemoteActionsDebugMode) {
		return {
			product: 'jira.preview',
			pollingInterval: DEBUG_MODE_POLLING_INTERVAL,
		};
	}
	return {
		product: 'jira',
		pollingInterval: DEFAULT_POLLING_INTERVAL,
	};
};

const matchBuildKey = (buildKey: string, buildKeyPattern: RegExp): number => {
	const buildNumber = buildKey.match(buildKeyPattern);
	if (buildNumber) {
		return parseInt(buildNumber[1], 10);
	}
	throw new Error('Invalid build key');
};

/**
 * Returns the build type and build number from a given buil key. This approach
 * safeguards against unintended overrides in build prioritization, particularly
 * for test, branch, and hotfix builds.
 */
export const getBuildInfoFromKey = (
	buildKey: string,
): {
	buildNumber: number;
	buildType: string;
} => {
	if (buildKey === 'JF-TEST') {
		return {
			buildNumber: Number.MAX_VALUE,
			buildType: 'local',
		};
	}
	if (buildKey.startsWith('MRJF-BB')) {
		return {
			buildNumber: Number.MAX_VALUE,
			buildType: 'branch',
		};
	}
	if (buildKey.startsWith('MRJF-PROD-ALT')) {
		return {
			buildNumber: matchBuildKey(buildKey, /^MRJF-PROD-ALT-(\d+)(-\w+)?$/),
			buildType: 'alt',
		};
	}
	if (buildKey.startsWith('MRJF-PROD-HOTFIX')) {
		return {
			buildNumber: matchBuildKey(buildKey, /^MRJF-PROD-HOTFIX-(\d+)(-\w+)?$/),
			buildType: 'hotfix',
		};
	}

	return {
		buildNumber: matchBuildKey(buildKey, /^MRJF-PROD-(\d+)(-\w+)?$/),
		buildType: 'prod',
	};
};

const fireLifecycleAnalytics = (
	analyticsEvent: UIAnalyticsEvent,
	eventAction: string,
	attributes?: Record<string, string | number | boolean | string[] | undefined>,
) => {
	if (fg('remote-actions-lifecycle-analytics_9n9tr')) {
		fireOperationalAnalytics(analyticsEvent, `remoteAction ${eventAction}`, {
			source: 'remoteActions',

			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			frontendVersion: window.BUILD_KEY,
			...attributes,
		});
	}
};

export const getEndpointUrl = (isStaging: boolean, isFedrampPerimeter: boolean) => {
	if (isFedrampPerimeter) {
		if (isStaging) {
			return 'https://update-remote-action.stg.frontend.cdn.atlassian-us-gov-mod.com/assets';
		}
		return 'https://update-remote-action.frontend.cdn.atlassian-us-gov-mod.com/assets';
	}
	if (isStaging) {
		return 'https://update-remote-action.stg-east.frontend.public.atl-paas.net/assets';
	}
	return 'https://update-remote-action.prod-east.frontend.public.atl-paas.net/assets';
};

const RemoteActions = () => {
	const isStaging = useIsStagingOrDevOrLocal();
	const [debugModeProps, setDebugModeProps] = useState<DebugModeProps | null>(null);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	useEffect(() => {
		const props = getDebugModeProps(getDebugModeFromLocalStorage());
		// eslint-disable-next-line react-you-might-not-need-an-effect/no-initialize-state
		setDebugModeProps(props);
	}, []);

	const onLifecycleEvent: LifecycleEventHandler = useCallback(
		({ action, attributes }) => {
			fireLifecycleAnalytics(createAnalyticsEvent({}), action, attributes);
		},
		[createAnalyticsEvent],
	);

	const endpointUrl = getEndpointUrl(isStaging, isFedRamp());
	const productData = useMemo(
		() => ({
			getBuildKey: () => (typeof window !== 'undefined' ? window.BUILD_KEY : ''),
			getBuildInfoFromKey,
			getFeatureGateValue: fg,
			getFeatureFlagValue: ff,
		}),
		[],
	);

	if (!endpointUrl || !debugModeProps) {
		return null;
	}

	return (
		<RemoteActionsManager
			endpoint={endpointUrl}
			product={debugModeProps.product}
			productData={productData}
			pollingInterval={debugModeProps.pollingInterval}
			onLifecycleEvent={onLifecycleEvent}
		/>
	);
};

export default componentWithCondition(
	() => fg('remote_actions_-_killswitch_-_temporary'),
	RemoteActions,
	() => null,
);
